import { useState, useEffect, useRef } from 'react';
import { FileImage, Video, Music, Type, FileJson, Box, ChevronRight, Loader2 } from 'lucide-react';
import { PromptItemType } from './PromptItemNode';
import assetService, { Asset } from '../../../services/assetService';

interface PromptItemSelectorProps {
  onSelect: (type: PromptItemType, label: string, assetId?: string) => void;
  onCancel: () => void;
  projectId: string;
}

type CategoryKey = 'images' | 'video' | 'audio' | 'fonts' | 'data' | '3d';

interface Category {
  key: CategoryKey;
  label: string;
  icon: typeof FileImage;
}

const categories: Category[] = [
  { key: 'images', label: 'Images', icon: FileImage },
  { key: 'video', label: 'Videos', icon: Video },
  { key: 'audio', label: 'Audio', icon: Music },
  { key: 'fonts', label: 'Fonts', icon: Type },
  { key: 'data', label: 'Data', icon: FileJson },
  { key: '3d', label: '3D Models', icon: Box },
];

export default function PromptItemSelector({ onSelect, onCancel, projectId }: PromptItemSelectorProps) {
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedCategory) {
      loadAssets(selectedCategory);
    }
  }, [selectedCategory]);

  // Handle click outside to dismiss
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onCancel();
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCancel]);

  const loadAssets = async (category: CategoryKey) => {
    setLoading(true);
    setError(null);
    try {
      const fetchedAssets = await assetService.listAssets(projectId, category, false);
      setAssets(fetchedAssets);
    } catch (err) {
      console.error('Failed to load assets:', err);
      setError('Failed to load assets');
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category: CategoryKey) => {
    setSelectedCategory(category);
  };

  const handleAssetClick = (asset: Asset) => {
    // Map category to specific asset type for color coding
    const assetTypeMap: Record<string, PromptItemType> = {
      'images': 'asset-image',
      'video': 'asset-video',
      'audio': 'asset-audio',
      'fonts': 'asset-font',
      'data': 'asset-data',
      '3d': 'asset-3d',
    };
    
    const itemType = assetTypeMap[asset.category || ''] || 'asset';
    onSelect(itemType, asset.filename, asset.id);
  };

  const handleBack = () => {
    setSelectedCategory(null);
    setAssets([]);
    setError(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getAssetPreview = (asset: Asset) => {
    // For images, show thumbnail if URL is available
    if (asset.category === 'images' && asset.url) {
      return (
        <img 
          src={asset.url} 
          alt={asset.filename}
          className="w-10 h-10 object-cover rounded"
        />
      );
    }
    
    // For other types, show icon
    const category = categories.find(c => c.key === asset.category);
    const Icon = category?.icon || FileImage;
    return (
      <div className="w-10 h-10 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded">
        <Icon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
      </div>
    );
  };

  const getAssetMetadata = (asset: Asset): string => {
    if (!asset.metadata) return '';
    
    const meta = asset.metadata;
    if (meta.image && meta.image.width && meta.image.height) {
      return `${meta.image.width}×${meta.image.height}`;
    } else if (meta.audio && meta.audio.duration) {
      const mins = Math.floor(meta.audio.duration / 60);
      const secs = Math.floor(meta.audio.duration % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    } else if (meta.video && meta.video.duration) {
      const mins = Math.floor(meta.video.duration / 60);
      const secs = Math.floor(meta.video.duration % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    } else if (meta.font && meta.font.family) {
      return meta.font.family;
    }
    return '';
  };

  return (
    <div 
      ref={containerRef}
      className="absolute bottom-full left-10 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg w-80 z-50 overflow-hidden"
    >
      {!selectedCategory ? (
        // Level 1: Category selection
        <div className="p-2">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 px-2 py-1 mb-1">
            Select Asset Category
          </div>
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.key}
                onClick={() => handleCategoryClick(category.key)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-amber-600 dark:group-hover:text-amber-400" />
                  <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                    {category.label}
                  </span>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
              </button>
            );
          })}
        </div>
      ) : (
        // Level 2: Asset list
        <div className="flex flex-col h-full max-h-96">
          {/* Header */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <button
              onClick={handleBack}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
            </button>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {categories.find(c => c.key === selectedCategory)?.label}
            </span>
          </div>

          {/* Asset list */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
              </div>
            ) : error ? (
              <div className="px-3 py-4 text-sm text-red-600 dark:text-red-400 text-center">
                {error}
              </div>
            ) : assets.length === 0 ? (
              <div className="px-3 py-8 text-sm text-gray-500 dark:text-gray-400 text-center">
                No assets in this category
              </div>
            ) : (
              <div className="p-2">
                {assets.map((asset) => (
                  <button
                    key={asset.id}
                    onClick={() => handleAssetClick(asset)}
                    className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors group"
                  >
                    {getAssetPreview(asset)}
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-amber-700 dark:group-hover:text-amber-300">
                        {asset.filename}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                        {getAssetMetadata(asset) && (
                          <span>{getAssetMetadata(asset)}</span>
                        )}
                        {getAssetMetadata(asset) && asset.size && (
                          <span>•</span>
                        )}
                        {asset.size && <span>{asset.size}</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
